import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  FlexColumn,
  FlexRow,
  HashLength,
  SvgIcon,
  VerifiedIcon,
} from '@make-software/cspr-design';
import { deriveAccountInfo, isValidPublicKey } from '../../utils/account';
import {
  AccountInfoAccordion,
  AccountInfoTable,
} from '../account-info-accordion/account-info-accordion';
import DetailsTableRow from '../details-table-row/details-table-row';
import DetailsNameTableData from '../details-name-table-data/details-name-table-data';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';
import { DetailsLocation } from '../details-location/details-location';
import { useCreateAccountsInfoGetter } from '../../hooks';
import {
  HashFontSize,
  TableDataAccountInfo,
} from '../table-data-account-info/table-data-account-info';

const OwnerNodeHeader = styled('div')(({ theme }) =>
  theme.withMedia({
    padding: '12px 20px 12px 24px',
    lineHeight: '20px',
  })
);
const OwnerNodeCell = styled('div')(({ theme }) =>
  theme.withMedia({
    padding: '12px 20px 12px 0px',
    lineHeight: '20px',
  })
);

export const DetailsAccordionNodes = ({
  accordionTitle,
  otherNodes,
  isAccountVerified,
}: {
  accordionTitle: React.ReactElement;
  otherNodes;
  isAccountVerified;
}) => {
  const { t } = useTranslation();

  const nodesEmpty = !otherNodes?.filter((i) => isValidPublicKey(i.public_key))
    .length;

  const publicKeysList = nodesEmpty
    ? null
    : otherNodes.map((i) => i.public_key);

  const { getAccountInfo, loading: loadingAccountsInfo } =
    useCreateAccountsInfoGetter(publicKeysList);

  return nodesEmpty ? null : (
    <AccountInfoAccordion
      disableClickAway
      renderContent={() => (
        <AccountInfoTable
          renderData={() =>
            otherNodes
              ?.filter((i) => isValidPublicKey(i.public_key))
              .map((node, index) => {
                const publicKey: string = node.public_key!;
                const accountInfo = getAccountInfo(node.public_key);
                const accountInfoData = deriveAccountInfo(
                  accountInfo?.account_info ||
                    accountInfo?.centralized_account_info
                );
                const logo = accountInfoData?.logo;
                const csprName = accountInfo?.cspr_name;
                return (
                  <>
                    <DetailsTableRow key={node.public_key + '0'}>
                      <DetailsNameTableData>
                        <Trans t={t}>Node</Trans> {index + 1}
                      </DetailsNameTableData>
                      <DetailsValueTableData monotype>
                        <FlexRow align={'center'}>
                          <TableDataAccountInfo
                            loading={loadingAccountsInfo}
                            publicKey={publicKey}
                            hashLength={HashLength.FULL}
                            csprName={csprName}
                            copyNotifyingStyle={'full'}
                            avatarSize={'small'}
                            hashFontSize={HashFontSize.big}
                            validatorAccount={node.functionality?.indexOf(
                              'validator'
                            )}
                            logo={logo}
                          />
                          {isAccountVerified(publicKey) && (
                            <SvgIcon
                              src={VerifiedIcon}
                              role={'img'}
                              alt={'Verified icon'}
                              marginLeft
                            />
                          )}
                        </FlexRow>
                      </DetailsValueTableData>
                    </DetailsTableRow>
                    <DetailsTableRow key={node.public_key + '1'}>
                      <DetailsNameTableData verticalAlign={'baseline'}>
                        <FlexColumn>
                          {(node.functionality?.length || null) && (
                            <OwnerNodeHeader>
                              <Trans t={t}>Functionality</Trans>
                            </OwnerNodeHeader>
                          )}
                          {node.location?.country && (
                            <OwnerNodeHeader>
                              <Trans t={t}>Location</Trans>
                            </OwnerNodeHeader>
                          )}
                          {node.description && (
                            <OwnerNodeHeader>
                              <Trans t={t}>Description</Trans>
                            </OwnerNodeHeader>
                          )}
                        </FlexColumn>
                      </DetailsNameTableData>
                      <DetailsValueTableData>
                        <FlexColumn>
                          {(node.functionality?.length || null) && (
                            <OwnerNodeCell
                              style={{ textTransform: 'capitalize' }}
                            >
                              {node.functionality?.join(', ')}
                            </OwnerNodeCell>
                          )}
                          {node.location?.country && (
                            <OwnerNodeCell>
                              <DetailsLocation location={node?.location} />
                            </OwnerNodeCell>
                          )}
                          {node.description && (
                            <OwnerNodeCell>{node.description}</OwnerNodeCell>
                          )}
                        </FlexColumn>
                      </DetailsValueTableData>
                    </DetailsTableRow>
                  </>
                );
              })
          }
        />
      )}
    >
      {accordionTitle}
    </AccountInfoAccordion>
  );
};
